/**
 * <DenominationCounter> — PR2-1 · touch-first cash counting grid.
 * 48px min touch targets, +/- steppers, live total.
 * Emits { breakdown, totalMinor } via onChange.
 */
import { useMemo } from "react";
import { Plus, Minus } from "lucide-react";
import { NumberInput } from "@/components/ui/number-input";

const NOTES = ["2000", "500", "200", "100", "50", "20", "10", "5", "2", "1"];

export function DenominationCounter({
  value = {},   // { "500": 42, "coins": 1750, ... }
  onChange,
  testid = "denomination-counter",
}) {
  const totalMinor = useMemo(() => {
    let t = 0;
    for (const k of NOTES) {
      const n = Number(value[k] || 0);
      t += n * parseInt(k, 10) * 100;
    }
    t += Number(value.coins || 0);
    return t;
  }, [value]);

  const setKey = (k, next) => {
    const clean = Math.max(0, Math.floor(Number(next) || 0));
    onChange?.({ ...value, [k]: clean });
  };

  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {NOTES.map(k => {
          const count = Number(value[k] || 0);
          const subtotal = count * parseInt(k, 10) * 100;
          return (
            <div key={k}
              className="flex items-center gap-2 border-2 border-slate-200 rounded-lg p-2 min-h-[56px]"
              data-testid={`${testid}-row-${k}`}>
              <div className="w-16 text-[15px] font-bold text-right pr-2 border-r border-slate-200">
                ₹{parseInt(k, 10).toLocaleString("en-IN")}
              </div>
              <button
                type="button"
                onClick={() => setKey(k, count - 1)}
                className="min-w-[48px] min-h-[48px] rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center"
                data-testid={`${testid}-dec-${k}`}>
                <Minus className="w-4 h-4"/>
              </button>
              <NumberInput
                min={0}
                value={count}
                onChange={e => setKey(k, e.target.value)}
                className="flex-1 min-w-[64px] h-12 text-center border-2 border-slate-300 rounded-lg font-semibold text-[18px]"
                data-testid={`${testid}-input-${k}`}
              />
              <button
                type="button"
                onClick={() => setKey(k, count + 1)}
                className="min-w-[48px] min-h-[48px] rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center"
                data-testid={`${testid}-inc-${k}`}>
                <Plus className="w-4 h-4"/>
              </button>
              <div className="w-24 text-right text-[13px] text-slate-600 font-medium">
                {subtotal ? `₹${(subtotal / 100).toLocaleString("en-IN")}` : "—"}
              </div>
            </div>
          );
        })}
      </div>

      {/* Coins bucket — direct minor-unit entry */}
      <div className="flex items-center gap-2 border-2 border-slate-200 rounded-lg p-2 min-h-[56px]"
            data-testid={`${testid}-row-coins`}>
        <div className="w-16 text-[13px] font-bold text-right pr-2 border-r border-slate-200">Coins</div>
        <NumberInput
          min={0}
          step="0.01"
          value={value.coins ? (Number(value.coins) / 100).toString() : ""}
          onChange={e => onChange?.({ ...value, coins: Math.max(0, Math.round((Number(e.target.value) || 0) * 100)) })}
          placeholder="Total coin value (₹)"
          className="flex-1 h-12 text-center border-2 border-slate-300 rounded-lg font-semibold text-[18px]"
          data-testid={`${testid}-input-coins`}
        />
        <div className="w-24 text-right text-[13px] text-slate-600 font-medium">
          {value.coins ? `₹${(Number(value.coins) / 100).toLocaleString("en-IN")}` : "—"}
        </div>
      </div>

      <div className="flex items-center justify-between border-t-2 border-slate-300 pt-3 mt-2">
        <span className="text-[13px] uppercase tracking-wider text-slate-500 font-semibold">Counted Total</span>
        <span className="text-[22px] font-bold" data-testid={`${testid}-total`}>
          ₹{(totalMinor / 100).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      </div>
    </div>
  );
}

export default DenominationCounter;
